import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createUser, deactivateUser, listUsers, patchUser,
  WorkspaceUser,
} from '@/api/client';
import AppFooter from '@/components/layout/AppFooter';

import { apiErrorText } from '../../lib/describeApiError';
/**
 * /admin/users — admin-only user management. Server-side gated via
 * `require_admin`; the API returns 403 for non-admin. The component
 * surfaces that as an inline "Admin access required" state.
 *
 * Native HTML throughout — same pattern as DiagnosticPage / Settings,
 * sidesteps the untyped shadcn forwardRef wrappers.
 */

interface Props {
  currentUser?: { id: number; email: string; role: string } | null;
  onBack?: () => void;
}

const ROLES: Array<'admin' | 'drafter' | 'reviewer'> = ['admin', 'drafter', 'reviewer'];

const inputClass =
  'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1.5 ' +
  'text-sm focus-visible:outline-none focus-visible:ring-2 ' +
  'focus-visible:ring-ring disabled:opacity-50';
const primaryBtn =
  'inline-flex h-9 items-center justify-center rounded-md bg-primary px-3 ' +
  'text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50';
const outlineBtn =
  'inline-flex h-8 items-center justify-center rounded border border-input ' +
  'bg-background px-2 text-xs font-medium hover:bg-accent disabled:opacity-50';
const dangerBtn =
  'inline-flex h-8 items-center justify-center rounded border border-red-200 ' +
  'bg-red-50 px-2 text-xs font-medium text-red-700 hover:bg-red-100 disabled:opacity-50';

/** Compact "how long ago" for the draft-adoption columns. Absolute
 *  date stays available as a tooltip on the cell. */
function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const secs = Math.round((Date.now() - then) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.round(months / 12)}y ago`;
}

function StatusPill({ active }: { active: boolean }) {
  return (
    <span
      className={
        'px-2 py-0.5 text-[11px] rounded ' +
        (active
          ? 'bg-emerald-100 text-emerald-800'
          : 'bg-zinc-200 text-zinc-600')
      }
    >
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

export default function UsersPage({ currentUser, onBack }: Props) {
  const [users, setUsers] = useState<WorkspaceUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [showInvite, setShowInvite] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      setUsers(await listUsers());
    } catch (e: any) {
      if (e?.response?.status === 403) {
        setErr('Admin access required.');
      } else {
        setErr(apiErrorText(e, 'Failed to load users.'));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const sorted = useMemo(() => {
    return [...users].sort(
      (a, b) => (a.created_at || '').localeCompare(b.created_at || ''),
    );
  }, [users]);

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto p-6 text-sm text-zinc-500">Loading users…</div>
    );
  }
  if (err) {
    return (
      <div className="max-w-5xl mx-auto p-6 text-sm">
        {onBack && (
          <button onClick={onBack} className="text-zinc-500 mb-3">← Back</button>
        )}
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-red-800">{err}</div>
      </div>
    );
  }

  return (
    <div data-testid="users-page" className="max-w-5xl mx-auto p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {onBack && (
            <button onClick={onBack} aria-label="Back" className="text-zinc-500 hover:text-zinc-900">
              ←
            </button>
          )}
          <h1 className="text-2xl font-semibold text-zinc-900" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            User Management
          </h1>
          <span className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">
            admin only
          </span>
        </div>
        <button
          type="button"
          data-testid="invite-user-button"
          className={primaryBtn}
          onClick={() => setShowInvite((s) => !s)}
        >
          {showInvite ? 'Cancel' : '+ Invite User'}
        </button>
      </div>

      {showInvite && (
        <InviteForm
          onCancel={() => setShowInvite(false)}
          onCreated={async () => {
            setShowInvite(false);
            await load();
          }}
        />
      )}

      <div className="border border-zinc-200 rounded-md bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-zinc-500 text-xs">
            <tr className="border-b border-zinc-100">
              <th className="text-left p-3">Email</th>
              <th className="text-left p-3">Role</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Drafts</th>
              <th className="text-left p-3">Last draft</th>
              <th className="text-left p-3">Last login</th>
              <th className="text-left p-3">Joined</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {sorted.map((u) => (
              <UserRow
                key={u.id}
                user={u}
                isSelf={currentUser?.id === u.id}
                onReload={load}
              />
            ))}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={8} className="p-6 text-center text-zinc-500">
                  No users yet. Click “Invite User” to add one.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="text-xs text-zinc-500 border border-zinc-200 bg-zinc-50 rounded p-3">
        <strong>Note:</strong> no invite email is sent in v1. Share the app URL — the invited user enters their email on the login screen and receives an OTP immediately. Deactivated accounts can still request an OTP, but <code>verify-otp</code> returns 403 with a message to contact an administrator.
      </div>
      <AppFooter />
    </div>
  );
}

function InviteForm({
  onCancel, onCreated,
}: { onCancel: () => void; onCreated: () => void }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'drafter' | 'reviewer'>('drafter');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    setOk(null);
    try {
      const u = await createUser(email.trim(), role);
      setOk(`Added ${u.email}. They can log in via OTP immediately.`);
      setEmail('');
      onCreated();
    } catch (e: any) {
      setErr(apiErrorText(e, 'Failed to create user.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="border border-zinc-200 rounded-md bg-white p-4 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-3 items-end">
        <div>
          <label className="text-xs text-zinc-500">Email</label>
          <input
            className={inputClass}
            type="email"
            data-testid="invite-email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="new.user@yourfirm.com"
            disabled={saving}
          />
        </div>
        <div>
          <label className="text-xs text-zinc-500">Role</label>
          <select
            className={inputClass}
            data-testid="invite-role"
            value={role}
            onChange={(e) => setRole(e.target.value as any)}
            disabled={saving}
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>
        <div className="flex gap-2">
          <button type="submit" className={primaryBtn} disabled={saving}>
            {saving ? 'Sending…' : 'Send Invite'}
          </button>
          <button type="button" className={outlineBtn} onClick={onCancel} disabled={saving}>
            Cancel
          </button>
        </div>
      </div>
      {err && <div className="text-xs text-red-700">{err}</div>}
      {ok && <div className="text-xs text-emerald-700">{ok}</div>}
    </form>
  );
}

function UserRow({
  user, isSelf, onReload,
}: { user: WorkspaceUser; isSelf: boolean; onReload: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const changeRole = async (newRole: string) => {
    if (newRole === user.role) return;
    setErr(null);
    setBusy(true);
    try {
      await patchUser(user.id, { role: newRole });
      await onReload();
    } catch (e: any) {
      setErr(apiErrorText(e, 'Role change failed'));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async () => {
    const next = !user.is_active;
    if (!next) {
      // Deactivation — confirm first.
      if (!window.confirm(`Deactivate ${user.email}? They will not be able to log in.`)) {
        return;
      }
    }
    setErr(null);
    setBusy(true);
    try {
      await patchUser(user.id, { is_active: next });
      await onReload();
    } catch (e: any) {
      setErr(apiErrorText(e, 'Action failed'));
    } finally {
      setBusy(false);
    }
  };

  const lockedTip = 'Cannot modify your own account';

  return (
    <>
      <tr className={busy ? 'opacity-60' : ''}>
        <td className="p-3">
          <div className="text-sm text-zinc-900">{user.email}</div>
          <div className="text-[11px] font-mono text-zinc-400">#{user.id}</div>
        </td>
        <td className="p-3">
          <select
            className={inputClass + ' w-auto'}
            value={user.role}
            data-testid={`role-select-${user.id}`}
            disabled={isSelf || busy}
            title={isSelf ? lockedTip : undefined}
            onChange={(e) => changeRole(e.target.value)}
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </td>
        <td className="p-3"><StatusPill active={user.is_active} /></td>
        <td className="p-3 text-right">
          {user.drafts_count > 0 ? (
            <span
              className="inline-block min-w-[1.5rem] text-sm font-medium text-zinc-900"
              title={`${user.drafts_count} draft${user.drafts_count === 1 ? '' : 's'} owned`}
            >
              {user.drafts_count}
            </span>
          ) : (
            <span className="text-xs text-zinc-400" title="No drafts yet">0</span>
          )}
        </td>
        <td className="p-3 text-xs text-zinc-500">
          {user.last_draft_at ? (
            <span title={new Date(user.last_draft_at).toLocaleString()}>
              {relativeTime(user.last_draft_at)}
            </span>
          ) : (
            '—'
          )}
        </td>
        <td className="p-3 text-xs text-zinc-500">
          {user.last_login ? new Date(user.last_login).toLocaleString() : '—'}
        </td>
        <td className="p-3 text-xs text-zinc-500">
          {user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}
        </td>
        <td className="p-3 text-right">
          {isSelf ? (
            <span className="text-[11px] text-zinc-400" title={lockedTip}>You</span>
          ) : user.is_active ? (
            <button
              type="button"
              data-testid={`deactivate-${user.id}`}
              className={dangerBtn}
              onClick={toggleActive}
              disabled={busy}
            >
              Deactivate
            </button>
          ) : (
            <button
              type="button"
              data-testid={`reactivate-${user.id}`}
              className={outlineBtn}
              onClick={toggleActive}
              disabled={busy}
            >
              Reactivate
            </button>
          )}
        </td>
      </tr>
      {err && (
        <tr>
          <td colSpan={8} className="px-3 pb-2 text-xs text-red-700">{err}</td>
        </tr>
      )}
    </>
  );
}
